import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelUnpair,
  getAccountDevices,
  setDeviceNickname,
  unpairDevice,
  type AccountDevice,
} from '../transport/api'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { fetchChannelVersion, requestUpdate, versionState } from '../servers/edgeVersion'
import { runningOf, healthOf, updateStatus } from '../servers/updateHealth'
import { deviceDisplayName } from '../transport/deviceName'
import { deviceAgeS, deviceLiveness } from '../servers/serverList'
import { relativeAge } from '../ui/relativeTime'
import { nodesUnderHomes } from './machineNodes'

/**
 * MACHINES — the paired-device list and its per-machine sheet, one of the main
 * menu's screens. Split out of ServerPanel.tsx in M6 (拆面板, 2026-09-03), moved
 * VERBATIM: the census measured that the menu's six screens share almost no state,
 * and they shared a 1,028-line file anyway, so every screen's tests paid the whole
 * file's mock bill. ServerPanel keeps the frame and the composition.
 */
export function MachinesSection() {
  const qc = useQueryClient()
  // Its OWN key — NOT ['account-devices'], which the account WS owns as a
  // ServerList. Sharing it was the whole bug: the WS overwrote this fetch's
  // {email, devices} with a ServerList object, blanking then crashing the list.
  const devQ = useQuery({ queryKey: ['menu-machines'], queryFn: getAccountDevices })
  /*
   * What the channel is serving, asked ONCE for the whole list rather than per
   * row: staleness is one comparison against one number, and N fetches of the
   * same manifest would be N chances to disagree with each other on screen.
   */
  const channelQ = useQuery({
    queryKey: ['edge-channel'],
    queryFn: fetchChannelVersion,
    staleTime: 60_000,
  })
  const channel = channelQ.data ?? null
  /*
   * The ROW is the button (owner, 2026-08-19: Machine 那一欄本身就是按鈕,
   * 一個按鈕都不需要新增). Tapping a machine opens its sheet, and Rename /
   * Unpair live in there — the list itself carries no controls, so it reads
   * as a list and still fits a phone column with long hostnames.
   */
  const [open, setOpen] = useState<string | null>(null)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['menu-machines'] })

  /*
   * The key is the menu's own now, so `data` is always this fetch's
   * {email, devices}. The Array.isArray guard STAYS regardless — a `.map` in a
   * settings list must never be one shape-drift away from a white screen
   * (owner, 2026-08-09, `_.map is not a function`); `?? []` alone would pass a
   * non-array `.devices` straight through.
   */
  const raw = devQ.data as { email?: string; devices?: unknown } | undefined
  const devices: AccountDevice[] = Array.isArray(raw?.devices)
    ? (raw.devices as AccountDevice[])
    : []
  const ownerEmail = raw?.email
  const opened = devices.find((d) => d.device_id === open) ?? null

  return (
    <>
      <div className="gh-sec">Machines</div>
      {devQ.isPending && <p className="gh-note">Checking…</p>}
      {devQ.isError && <p className="gh-note">Could not list machines — {String(devQ.error)}</p>}
      {!devQ.isPending && !devQ.isError && devices.length === 0 && (
        /* Named, because an empty list is usually TRUE — about the wrong
           account. Naming whose emptiness this is turns "bug" into "sign in
           with the other Google account". */
        <p className="gh-note">
          No machines paired on {ownerEmail ?? 'this account'}.
        </p>
      )}
      {nodesUnderHomes(devices).map(({ d, homeOf }) => {
        const nickname = (d.nickname ?? '').trim()
        /* NOT the display name: this is what the MACHINE calls itself, shown beside a
           nickname so a rename cannot cost the row its identity. */
        const hostname = d.hostname || d.device_id.slice(0, 8)
        const name = deviceDisplayName(d)
        const pending = d.state === 'pending_unpair'
        /*
         * ONLINE / OFFLINE — owner, 2026-08-09: 這個 in use 的 label 沒意義，直接
         * 拿掉，指出 online / offline 即可. The BFF reports `live` in three states:
         * true = a link is up now; false = never/known-away; null = this instance
         * can't tell (a sibling may hold the link), so fall back to how recently
         * the machine was last seen.
         */
        /*
         * ASKS, rather than deciding. This was its own rule — `Date.now()` against a
         * server-stamped last_seen, 120s instead of 90, no state check, and `live: null`
         * counted as online. Four ways to disagree with the rail about one machine, on
         * the sheet you open to decide whether that machine is reachable.
         *
         * Three states now, like the rail: `unknown` is not `offline`. On this
         * deployment it is the STEADY state — the webapp's BFF instance does not hold a
         * machine's link, so `live` arrives null — and painting that grey said "gone"
         * about every working machine.
         */
        const ageS = deviceAgeS(d)
        const live = deviceLiveness(d, ageS)
        return (
          <button
            type="button"
            className={homeOf ? 'mch-row mch-node' : 'mch-row'}
            key={d.device_id}
            aria-label={`Manage ${name}`}
            onClick={() => setOpen(d.device_id)}
          >
            <span
              className={`mch-dot ${live === 'online' ? 'live' : live === 'offline' ? 'off' : 'unknown'}`}
              aria-hidden="true"
            />
            <span className="mch-meta">
              <span className="mch-name" title={nickname ? `${hostname} · ${d.device_id}` : d.device_id}>
                {name}
                {/* The nickname ALONE — owner, 2026-09-10: 機器如果被取了別名, 那就只
                    需要顯示別名就好, 原本別名的目的就是要縮短名稱. The hostname used
                    to be printed beside it in parentheses, which handed back the
                    length the nickname was chosen to remove (a cloud box's hostname
                    is forty characters of instance id). The hostname is still one
                    tap away: the management sheet shows it as the nickname field's
                    placeholder, and it rides in this row's tooltip. */}
                {/*
                  * UNKNOWN IS NOT OFFLINE, and this used to say it was. The word came
                  * from the same binary as the dot, so the two never contradicted each
                  * other — they were simply both wrong together, calling every machine
                  * whose link this BFF instance does not hold "offline". That is the
                  * steady state here, so the sheet told you your working fleet was down.
                  *
                  * Unworded when unknown, exactly like the rail: a word on every row
                  * that is the same word on every row carries no information, and this
                  * one carried a false one.
                  */}
                {live !== 'unknown' ? (
                  <span className={`mch-status ${live === 'online' ? 'on' : 'off'}`}>
                    {' · '}
                    {live}
                  </span>
                ) : (
                  /*
                   * THE AGE AS DETAIL, never as a verdict — which is what it used to be.
                   * The old rule read `last_seen` and CONCLUDED "online" from it, using
                   * the browser's clock, and that conclusion is what had to go. The
                   * information behind it did not: "last seen 4s ago" is genuinely
                   * useful when nobody here holds the link, and it says exactly what it
                   * knows without claiming the machine answered.
                   *
                   * Same shape as the rail, which shows the age under an unworded
                   * verdict for the same reason.
                   */
                  <span className="mch-status">
                    {' · '}
                    {Number.isFinite(ageS) ? `last seen ${relativeAge(ageS)}` : 'never connected'}
                  </span>
                )}
                {/* The BUILD it is running, next to whether it is up — the two
                    questions asked together whenever a machine misbehaves. An
                    edge too old to report one shows nothing rather than a guess
                    (owner, 2026-08-20). */}
                {(() => {
                  /* The SAME value the status line reasons from — see runningOf. The
                     badge used `d.version` while the line used update_health.running,
                     so one row could say "(out of date)" and "nothing to report" at
                     once, which is what the owner was reading on gmi. */
                  const run = runningOf(healthOf(d), d.version)
                  return run ? (
                    <span className={`mch-ver ${versionState(run, channel)}`}>
                      {' · '}
                      {run}
                      {versionState(run, channel) === 'stale' && ' (out of date)'}
                    </span>
                  ) : null
                })()}
              </span>
              {/* ITS OWN LINE, like `pending`, and for the same reason: this rail is
                  208px and the first line already ends in an ellipsis mid-hash. A mark
                  appended after the version is a mark nobody on a phone ever sees —
                  the e2e read it out of innerText and passed while the screenshot
                  showed it cut off.

                  A machine pinned on purpose reads 'pinned', not a warning: aowl is
                  behind because somebody chose that, and colouring it like a fault is
                  how the colour stops being read on the machines that are stuck. */}
              {(() => {
                const u = updateStatus(healthOf(d), d.version, channel)
                /*
                 * THE MACHINE'S OWN SENTENCE, not one of two guesses about it.
                 *
                 * Owner, 2026-09-17: gmi read "auto-update off" while its own
                 * update_health said updater=on, blocked=null, no error. The label was
                 * wrong by construction: this rendered `warn ? A : B`, so every one of
                 * the THREE `note` states printed "auto-update off" — including "this
                 * build does not report why" and "an update is on its way", which are
                 * not that and are not even about the updater being off.
                 *
                 * updateHealth.ts exists to turn the report into exactly one sentence,
                 * and the row was discarding it. `line` is empty only when the tone is
                 * ok, which returns above, so there is always something to say.
                 */
                return u.tone === 'ok' ? null : (
                  <span className={`mch-up mch-up-${u.tone}`}>{u.line}</span>
                )
              })()}
              {/* Whose node it is — without it a cluster's compute node reads as a second
                  pairing of its login node (owner, 2026-10-21; machineNodes.ts). */}
              {homeOf && <span className="mch-of">node of {deviceDisplayName(homeOf)}</span>}
              {pending && <span className="mch-pending">pending unpair — takes effect when it next connects</span>}
            </span>
            <Icon name="chevron_right" size={16} />
          </button>
        )
      })}
      {opened && (
        // Keyed so switching machines remounts the sheet — a draft typed for one
        // machine must never carry into another's input.
        <MachineSheet
          key={opened.device_id}
          device={opened}
          channel={channel}
          onClose={() => setOpen(null)}
          onChanged={refresh}
        />
      )}
    </>
  )
}

/**
 * One machine's management sheet: status, nickname, unpair — reached by tapping
 * the row. Rename is a NICKNAME (owner, 2026-08-19): the machine keeps its
 * hostname; the account stores its own word for it, keyed by device_id, and the
 * BFF fans the change out on the WS devices frames so every open app re-names
 * the machine at once. Errors render HERE, beside the action that failed — the
 * first cut printed them below the whole list, where a failed Save read as
 * "nothing happened" (owner: 按下 Save 並沒有辦法儲存設定).
 */
function MachineSheet({
  device,
  channel,
  onClose,
  onChanged,
}: {
  device: AccountDevice
  /** What the channel serves, or null when we could not read it. */
  channel: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const nickname = (device.nickname ?? '').trim()
  // See the row above: kept apart from `name` on purpose — the rename field uses it as
  // its placeholder and the sheet prints it, both of which mean the machine's own name.
  const hostname = device.hostname || device.device_id.slice(0, 8)
  const name = deviceDisplayName(device)
  const pending = device.state === 'pending_unpair'
  /* The build it is running NOW — see runningOf. `device.version` is what the edge
     reported when it last connected, and an edge that has updated since keeps that
     row until it reconnects; reading it here made the sheet contradict its own
     next line ("Running <old> — out of date" above "nothing to report"). */
  const run = runningOf(healthOf(device), device.version)
  const state = versionState(run, channel)
  const up = updateStatus(healthOf(device), device.version, channel)
  const [upErr, setUpErr] = useState<string | null>(null)
  /** null = not asked yet; otherwise WHICH yes the machine gave. */
  const [upDone, setUpDone] = useState<'started' | 'busy' | null>(null)
  const update = useMutation({
    mutationFn: () => requestUpdate(device.device_id),
    onSuccess: (r) => {
      setUpDone(r.busy || !r.started ? 'busy' : 'started')
      // The list re-reads so the new build appears when the machine comes back.
      onChanged()
    },
    onError: (e: unknown) =>
      setUpErr(e instanceof Error ? e.message : 'could not ask that machine to update'),
  })
  const [draft, setDraft] = useState(nickname)
  const [armed, setArmed] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const fail = (e: unknown) => setErr(String(e instanceof Error ? e.message : e))
  const done = () => {
    onChanged()
    onClose()
  }

  const rename = useMutation({
    // Empty clears — the row falls back to the hostname. Trim so a nickname of
    // spaces cannot make a machine visually nameless while comparing unequal.
    mutationFn: () => setDeviceNickname(device.device_id, draft.trim() || null),
    onSuccess: () => {
      // The sidebar's ServerList cache too, so the rename shows there without
      // waiting for the WS push.
      void qc.invalidateQueries({ queryKey: ['account-devices'] })
      done()
    },
    onError: fail,
  })
  const unpair = useMutation({ mutationFn: () => unpairDevice(device.device_id), onSuccess: done, onError: fail })
  const cancel = useMutation({ mutationFn: () => cancelUnpair(device.device_id), onSuccess: done, onError: fail })

  const unchanged = draft.trim() === nickname

  return (
    <Sheet title={name} onClose={onClose} className="mch-sheet">
      <p className="mchs-status">
        {hostname}
        {' · '}
        <span className="mchs-id">{device.device_id}</span>
      </p>

      <div className="mchs-block">
        <label className="mchs-label" htmlFor="mchsNick">Nickname</label>
        <div className="mchs-row">
          <input
            id="mchsNick"
            className="mch-rename"
            value={draft}
            placeholder={hostname}
            maxLength={64}
            disabled={rename.isPending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !unchanged) rename.mutate()
              if (e.key === 'Escape') onClose()
            }}
          />
          <button
            type="button"
            className="mch-btn"
            disabled={rename.isPending || unchanged}
            onClick={() => rename.mutate()}
          >
            {rename.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="mchs-hint">
          Shown on every app end instead of the hostname. Leave empty to go back to {hostname}.
        </p>
      </div>

      {/*
        * WHAT IT IS RUNNING, and a way to move it forward from here.
        *
        * owner, 2026-08-20, after a machine sat 15 commits behind with a dead
        * update channel and nothing on screen said so. The button names the
        * channel explicitly when it asks, because the machines that need it are
        * exactly the ones whose own channel is wrong.
        *
        * It reports STARTED, not DONE: the machine installs and then restarts,
        * so nothing it says afterwards can reach us. The proof is the version it
        * reports next — which this list is already showing.
        */}
      <div className="mchs-block">
        <div className="gh-sec">Agent build</div>
        <p className="mchs-hint">
          {run ? (
            <>
              Running <code>{run}</code>
              {state === 'stale' && channel && (
                <>
                  {' — out of date; '}
                  <code>{channel}</code> is current.
                </>
              )}
              {state === 'current' && ' — up to date.'}
              {state === 'unknown' && ' — cannot tell what the current build is right now.'}
            </>
          ) : (
            <>This machine has never reported its build — an older agent. Updating it will fix that.</>
          )}
        </p>
        {/* WHY it is not on the current build, in the machine's own words where it has
            them. "(out of date)" was the whole story for days while two machines sat
            stuck and nobody could ssh into either. See updateHealth.ts — the tone is
            deliberately not red for a machine that was pinned on purpose. */}
        {up.line && <p className={`mchs-hint mch-up-${up.tone}`}>{up.line}</p>}
        {upErr && <p className="gh-note mch-err">{upErr}</p>}
        {upDone ? (
          <p className="mchs-hint">
            {upDone === 'busy'
              ? /*
                 * The edge answers `{ok:true, started:false, busy:true}` when one is
                 * already running — a 2xx, so this read "Update started" and hid the
                 * one state that matters while somebody is debugging an update that
                 * will not land (owner, 2026-08-22). Two states, two sentences.
                 */
                'That machine is already updating — this did not start a second one. The build shown here changes when it comes back.'
              : 'Update started. It installs and restarts itself; the build shown here changes when it comes back.'}
          </p>
        ) : (
          <button
            type="button"
            className="mch-btn"
            disabled={update.isPending}
            onClick={() => {
              setUpErr(null)
              update.mutate()
            }}
          >
            {update.isPending ? 'Asking…' : state === 'stale' ? 'Update now' : 'Update anyway'}
          </button>
        )}
      </div>

      <div className="mchs-block">
        {pending ? (
          <>
            <p className="mchs-hint">Pending unpair — takes effect when it next connects.</p>
            <button
              type="button"
              className="mch-btn"
              disabled={cancel.isPending}
              onClick={() => {
                setErr(null)
                cancel.mutate()
              }}
            >
              Cancel unpair
            </button>
          </>
        ) : (
          <button
            type="button"
            className={`mch-btn${armed ? ' danger' : ''}`}
            disabled={unpair.isPending}
            onClick={() => {
              setErr(null)
              if (!armed) {
                setArmed(true)
                window.setTimeout(() => setArmed(false), 3000)
                return
              }
              unpair.mutate()
            }}
          >
            {armed ? `Really unpair ${name}?` : 'Unpair'}
          </button>
        )}
      </div>

      {err && <p className="gh-note mch-err">{err}</p>}
    </Sheet>
  )
}

/**
 * Friends attached to the account, each removable from HERE rather than from the
 * messaging list — owner, 2026-08-19: 好友選單裡置入解除風險太高 (誤觸). Unfriending
 * sits beside Unpair because both are account management, reached deliberately.
 *
 * DOUBLE confirmation, stronger than Machines' single arm: Remove → "Remove X?" →
 * "Really? both lose it" → fires. Two deliberate confirms, each with a Cancel, plus a
 * 4s idle auto-disarm so a half-finished tap never lingers armed. The removal itself
 * is one-sided + silent (the BFF dissolves the mutual edge); the friction only exists
 * to stop a mis-tap.
 */
